import { useContext, useState } from 'react';
import { Button, CircularProgress, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useApolloClient } from '@apollo/client/react';
import { FunnelCart } from 'src/ts/types/cart';
import UserContext from 'src/context/user';
import { setDiscountOnCart } from '../_utils/discounts/set-discount';

interface Props {
  cart: FunnelCart;
  setCart: React.Dispatch<React.SetStateAction<FunnelCart | null>>;
}

const DiscountCodeInput: React.FC<Props> = ({ cart, setCart }) => {
  const apolloClient = useApolloClient();
  const userContext = useContext(UserContext);

  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingCode, setIsApplyingCode] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);

  const handleApplyCode = async () => {
    const code = promoCode.trim();
    if (!code) return;

    if (!userContext) {
      setInputError('Failed to apply promo code');
      return;
    }

    setIsApplyingCode(true);
    setInputError(null);

    const result = await setDiscountOnCart({
      cart,
      discountCode: code,
      apolloClient,
      userContext,
    });

    if (result.success) {
      setCart(result.cart);
      setPromoCode('');
      setShowPromoInput(false);
    } else {
      setInputError(result.error);
    }

    setIsApplyingCode(false);
  };

  if (showPromoInput) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Promo code"
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value);
              if (inputError) {
                setInputError(null);
              }
            }}
            onKeyDown={(e) =>
              e.key === 'Enter' && !isApplyingCode && handleApplyCode()
            }
            disabled={isApplyingCode}
            error={!!inputError}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            size="medium"
            onClick={handleApplyCode}
            disabled={!promoCode.trim() || isApplyingCode}
            sx={{ textTransform: 'none' }}
          >
            {isApplyingCode ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        </Box>
        <Typography variant="caption" color="error">
          {inputError}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      variant="body1"
      sx={{
        cursor: 'pointer',
      }}
      onClick={() => setShowPromoInput(true)}
    >
      + Add promo code
    </Typography>
  );
};

export default DiscountCodeInput;
